import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../config';

/**
 * Commercial state for the whole app: the workspace's plan, the trial clock,
 * live meters, and whatever limit the API last refused.
 *
 * Pages read it for banners and to hide what the plan does not include. The
 * server is still the source of truth: gates return 402 and the interceptor in
 * config.js fires `caply:limit`, which lands here as `limit`.
 */
const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const [billing, setBilling] = useState(null);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/api/billing');
      setBilling(data);
      return data;
    } catch (err) {
      // A workspace nobody has sold to yet must never see a broken shell.
      if (err?.response?.status !== 402) console.warn('Billing state unavailable', err?.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onLimit = (event) => {
      setLimit(event.detail || {});
      refresh();
    };
    window.addEventListener('caply:limit', onLimit);
    return () => window.removeEventListener('caply:limit', onLimit);
  }, [refresh]);

  const can = useCallback((feature) => Boolean(billing?.features?.[feature]), [billing]);

  return (
    <BillingContext.Provider
      value={{ billing, loading, refresh, limit, can, dismissLimit: () => setLimit(null) }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  return useContext(BillingContext) || { billing: null, loading: false, can: () => true };
}

export default BillingContext;
